import React, { useState } from 'react';
import './OrderRow.css';
import { Order, OrderStatus } from '../../services/api';

interface OrderRowProps {
  order: Order;
  onMarkShipped: (trackingNumber: string) => void | Promise<void>;
  onMarkDelivered: () => void | Promise<void>;
  onRefund: () => void | Promise<void>;
  onOpenDetail: () => void;
}

const STATUS_COLORS: Record<OrderStatus, string> = {
  pending: 'status-pending',
  paid: 'status-paid',
  processing: 'status-paid',
  shipped: 'status-shipped',
  delivered: 'status-delivered',
  cancelled: 'status-cancelled',
  refunded: 'status-refunded',
};

function formatMoney(order: Order): string {
  if (typeof order.total_cents === 'number') {
    return `$${(order.total_cents / 100).toFixed(2)}`;
  }
  return `$${Number(order.total || 0).toFixed(2)}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const OrderRow: React.FC<OrderRowProps> = ({
  order,
  onMarkShipped,
  onMarkDelivered,
  onRefund,
  onOpenDetail,
}) => {
  const [showShipInput, setShowShipInput] = useState(false);
  const [tracking, setTracking] = useState(order.tracking_number || '');
  const [submitting, setSubmitting] = useState(false);

  const itemCount = order.items?.reduce((acc, it) => acc + (it.quantity || 0), 0) || 0;
  const shortId = order.id.slice(-8);

  const handleShip = async () => {
    if (!tracking.trim()) return;
    setSubmitting(true);
    try {
      await onMarkShipped(tracking.trim());
      setShowShipInput(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="order-row" role="article">
      <button type="button" className="order-row-main" onClick={onOpenDetail}>
        <div className="order-row-id">
          <span className="order-id-label">Order</span>
          <span className="order-id-value">#{shortId}</span>
        </div>
        <div className="order-row-meta">
          <span className="order-date">{formatDate(order.created_at)}</span>
          <span className="order-item-count">{itemCount} item{itemCount === 1 ? '' : 's'}</span>
        </div>
        <div className="order-row-total">{formatMoney(order)}</div>
        <span className={`status-pill ${STATUS_COLORS[order.status] || 'status-pending'}`}>
          {order.status}
        </span>
      </button>

      <div className="order-row-actions" onClick={e => e.stopPropagation()}>
        {(order.status === 'paid' || order.status === 'processing') && !showShipInput && (
          <button className="order-action ship" onClick={() => setShowShipInput(true)}>
            Mark Shipped
          </button>
        )}
        {showShipInput && (
          <div className="ship-input-row">
            <input
              type="text"
              placeholder="Tracking #"
              value={tracking}
              onChange={e => setTracking(e.target.value)}
              autoFocus
            />
            <button
              className="order-action ship confirm"
              onClick={handleShip}
              disabled={!tracking.trim() || submitting}
            >
              {submitting ? '…' : 'Save'}
            </button>
            <button className="order-action ghost" onClick={() => setShowShipInput(false)}>
              Cancel
            </button>
          </div>
        )}
        {order.status === 'shipped' && (
          <button className="order-action deliver" onClick={() => onMarkDelivered()}>
            Mark Delivered
          </button>
        )}
        {(order.status === 'paid' || order.status === 'shipped' || order.status === 'delivered') && (
          <button
            className="order-action refund"
            onClick={() => {
              if (window.confirm(`Refund order #${order.id.slice(0, 8)}? This cannot be undone.`)) {
                onRefund();
              }
            }}
          >
            Refund
          </button>
        )}
      </div>
    </div>
  );
};

export default OrderRow;
